import {Injectable} from "@angular/core";
import {DecimalPipe} from "@angular/common";
import {SubmissionRule, SubmissionRuleActions, SubmissionRuleConditions} from "@services/template-data.service";
import {SCALE_MULTIPLIERS, ScaleType} from "@services/rounding.service";

export interface TemplateSubmissionRulesByRef {
  [templateItemRef: string]: SubmissionRule[];
}

export interface SubmissionRuleEvaluationResult {
  action: SubmissionRuleActions.HARD_STOP | SubmissionRuleActions.SOFT_STOP;
  message: string;
}

export const UserFacingSubmissionRuleConditions: Record<SubmissionRuleConditions, string> = {
  [SubmissionRuleConditions.EQUAL_TO]: "not be equal to",
  [SubmissionRuleConditions.NOT_EQUAL_TO]: "be equal to",
  [SubmissionRuleConditions.GREATER_THAN]: "be less than or equal to",
  [SubmissionRuleConditions.LESS_THAN]: "be greater than or equal to",
  [SubmissionRuleConditions.GREATER_THAN_OR_EQUAL_TO]: "less than",
  [SubmissionRuleConditions.LESS_THAN_OR_EQUAL_TO]: "greater than"
};

@Injectable()
export class SubmissionRulesService {

  private decimalPipe: DecimalPipe;

  constructor() {
    this.decimalPipe = new DecimalPipe('en-US');
  }

  parseTemplateSubmissionRules(templateSubmissionRulesArray: SubmissionRule[]): TemplateSubmissionRulesByRef {
    const result: TemplateSubmissionRulesByRef = {};

    for (const rule of templateSubmissionRulesArray) {
      const templateItemRef = rule.templateItem;
      if (!result[templateItemRef]) {
        result[templateItemRef] = [];
      }
      result[templateItemRef].push(rule);
    }

    return result;
  }

  evaluateSubmissionRule(submissionRule: SubmissionRule, value: any, roundingFactor: ScaleType): SubmissionRuleEvaluationResult | null {
    if (!submissionRule.condition || !submissionRule.equation || value == null) {
      return null;
    }

    try {
      const conditionMet = this.evaluateExpression(submissionRule.condition, submissionRule.equation, value);
      if (conditionMet) {
        return this.buildSubmissionRuleEvaluationResult(submissionRule, roundingFactor);
      }
      return null;
    } catch (error) {
      console.error('Error evaluating submission rule:', error);
      return null;
    }
  }

  private evaluateExpression(condition: SubmissionRuleConditions, equation: string, value: any): boolean {
    try {
      const comparisonValue = parseFloat(equation);
      if (isNaN(comparisonValue)) {
        console.error('Invalid equation value:', equation);
        return false;
      }

      const numericValue = parseFloat(String(value));
      if (isNaN(numericValue)) {
        console.error('Invalid input value:', value);
        return false;
      }

      switch (condition) {
        case SubmissionRuleConditions.EQUAL_TO:
          return numericValue === comparisonValue;
        case SubmissionRuleConditions.NOT_EQUAL_TO:
          return numericValue !== comparisonValue;
        case SubmissionRuleConditions.GREATER_THAN:
          return numericValue > comparisonValue;
        case SubmissionRuleConditions.LESS_THAN:
          return numericValue < comparisonValue;
        case SubmissionRuleConditions.GREATER_THAN_OR_EQUAL_TO:
          return numericValue >= comparisonValue;
        case SubmissionRuleConditions.LESS_THAN_OR_EQUAL_TO:
          return numericValue <= comparisonValue;
        default:
          console.error('Unknown condition type:', condition);
          return false;
      }
    } catch (error) {
      console.error('Error evaluating expression:', error);
      return false;
    }
  }

  private buildSubmissionRuleEvaluationResult(submissionRule: SubmissionRule, roundingFactor: ScaleType): SubmissionRuleEvaluationResult {
    const conditionString = UserFacingSubmissionRuleConditions[submissionRule.condition];
    let requirementSuggestion = 'should';
    if (submissionRule.action === SubmissionRuleActions.HARD_STOP) {
      requirementSuggestion = 'must';
    }

    let comparisonValue = parseFloat(submissionRule.equation);
    const multiplier = SCALE_MULTIPLIERS[roundingFactor]
    const roundedValue = comparisonValue ? comparisonValue / multiplier : comparisonValue;
    const formattedValue = this.decimalPipe.transform(roundedValue, '1.0-20');
    return {
      message: `Value ${requirementSuggestion} ${conditionString} ${formattedValue}`,
      action: submissionRule.action
    }

  }
}
